import React, { useState, useEffect } from 'react';
import { Heart, Star, Users, Award } from 'lucide-react';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { EditableText, EditableImage } from '../components/InlineEdit';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutPage = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await axios.get(`${API}/staff`);
        setStaff(res.data.filter(s => s.is_active));
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    };
    fetchStaff();
  }, []);

  const primaryColor = settings?.primary_color || '#BFA05F';
  const secondaryColor = settings?.secondary_color || '#1A1815';
  const backgroundColor = settings?.background_color || '#F9F8F6';
  const textColor = settings?.text_color || '#1A1815';

  return (
    <div className="min-h-screen pt-24" style={{ backgroundColor }} data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
              <EditableText settingKey="brand_name" defaultValue="Lou's Way" />
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4" style={{ color: textColor }}>
              <EditableText settingKey={`about_title_${language}`} defaultValue={t('about.title')} />
            </h1>
            <p className="text-lg mt-6 leading-relaxed" style={{ color: `${textColor}80` }}>
              <EditableText settingKey={`about_subtitle_${language}`} defaultValue={t('about.subtitle')} multiline />
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="animate-fade-in-up">
              <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
                <EditableText settingKey={`story_label_${language}`} defaultValue={t('about.story_title')} />
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-normal mt-4" style={{ color: textColor }}>
                <EditableText settingKey={`story_title_${language}`} defaultValue={language === 'pt' ? 'Uma Jornada ' : 'A Journey of'} />{' '}
                <span className="italic" style={{ color: primaryColor }}>
                  <EditableText settingKey={`story_accent_${language}`} defaultValue={language === 'pt' ? 'Transformadora' : 'Transformation'} />
                </span>
              </h2>
              <p className="mt-6 leading-relaxed" style={{ color: `${textColor}80` }}>
                <EditableText 
                  settingKey={`about_text_${language}`} 
                  defaultValue={t('about.story_text')} 
                  multiline 
                />
              </p>
              <p className="mt-4 leading-relaxed" style={{ color: `${textColor}80` }}>
                <EditableText 
                  settingKey={`about_text_2_${language}`} 
                  defaultValue={language === 'pt' 
                    ? 'Mais do que um procedimento, entregamos uma experiência marcada por dedicação, carinho e compromisso com resultados que realçam a beleza natural de forma harmoniosa e personalizada.'
                    : 'More than just a procedure, we deliver an experience marked by dedication, care, and commitment to results that enhance natural beauty in a harmonious and personalized way.'}
                  multiline
                />
              </p>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="absolute -top-4 -left-4 w-24 h-24 rounded-full blur-2xl" style={{ backgroundColor: `${primaryColor}33` }} />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full blur-3xl" style={{ backgroundColor: `${primaryColor}1A` }} />
              <EditableImage
                settingKey="about_image"
                defaultSrc="https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770140406/WhatsApp_Image_2026-02-03_at_00.52.55_aelipg.jpg"
                alt="Lou's Way Team"
                imgClassName="rounded-3xl w-full h-[500px] object-cover shadow-xl relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: 'stat_1_value', label: 'stat_1_label', defaultValue: '10+', defaultLabel: language === 'pt' ? 'Anos de Experiência' : 'Years of Experience' },
              { value: 'stat_2_value', label: 'stat_2_label', defaultValue: '500+', defaultLabel: language === 'pt' ? 'Clientes Satisfeitas' : 'Happy Clients' },
              { value: 'stat_3_value', label: 'stat_3_label', defaultValue: '35+', defaultLabel: language === 'pt' ? 'Tratamentos' : 'Treatments' },
              { value: 'stat_4_value', label: 'stat_4_label', defaultValue: '5☆', defaultLabel: language === 'pt' ? 'Google' : 'Google' },
            ].map((stat, index) => (
              <div key={index} className="text-center animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                <p className="font-heading text-4xl md:text-5xl font-medium" style={{ color: primaryColor }}>
                  <EditableText settingKey={stat.value} defaultValue={stat.defaultValue} />
                </p>
                <p className="text-sm mt-2" style={{ color: `${textColor}80` }}>
                  <EditableText settingKey={stat.label} defaultValue={stat.defaultLabel} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* Team Section */}
<section className="py-16 md:py-24">
  <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
    <div className="text-center mb-16 animate-fade-in-up">
      <h2 className="font-heading text-3xl md:text-4xl font-normal mt-4" style={{ color: textColor }}>
        <EditableText settingKey={`team_title_${language}`} defaultValue={language === 'pt' ? 'Profissional Dedicada' : 'Dedicated Professional'} />
      </h2>
    </div>

    {/* MUDANÇA AQUI: Trocamos grid por flex para centralização perfeita quando há poucos itens */}
    <div className="flex flex-wrap justify-center gap-8">
      {staff.length > 0 ? staff.map((member, index) => (
        <div
          key={member.staff_id}
          className="group animate-fade-in-up w-full md:w-[calc(33.333%-2rem)] max-w-sm"
          style={{ animationDelay: `${index * 100}ms` }}
          data-testid={`team-member-${index}`}
        >
          {/* mx-auto garante que o card interno alinhe se o container for maior que ele */}
          <div className="relative overflow-hidden rounded-2xl mx-auto">
            <img
              src={member.photo && member.photo.startsWith('http') ? member.photo : 'https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770140207/lour-121_hn9zr2.jpg'}
              alt={member.name}
              className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.target.src = 'https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770140207/lour-121_hn9zr2.jpg'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1815]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          
          <div className="mt-6 text-center">
            <h3 className="font-heading text-xl font-medium" style={{ color: textColor }}>
              {member.name}
            </h3>
            <p className="text-sm mt-1" style={{ color: primaryColor }}>
              {member.specialties?.join(', ') || (language === 'pt' ? 'Especialista' : 'Specialist')}
            </p>
            {member[`bio_${language}`] && (
              <p className="text-sm mt-2" style={{ color: `${textColor}80` }}>{member[`bio_${language}`]}</p>
            )}
          </div>
        </div>
      )) : (
        <p className="text-center" style={{ color: `${textColor}80` }}>
          {language === 'pt' ? 'A nossa equipa será apresentada em breve.' : 'Our team will be presented soon.'}
        </p>
      )}
    </div>
  </div>
</section>
    </div>
  );
};

export default AboutPage;
